import { useEffect } from "react";
import { useCartStore } from "@/stores/cartStore";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Minus, Plus, Trash2, ShoppingBag, Truck, Loader2, Sparkles, Lock, ShieldCheck, RotateCcw } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { gtagBeginCheckout } from "@/lib/google-analytics";
import { ttqInitiateCheckout } from "@/lib/tiktok-pixel";
import { klaviyoTrackStartedCheckout } from "@/lib/klaviyo";
import { trackInitiateCheckout as trackInitiateCheckoutInternal } from "@/lib/analytics";

export function CartDrawer() {
  const { items, isOpen, closeCart, removeItem, updateQuantity, isLoading, isSyncing, getCheckoutUrl, syncCart } = useCartStore();

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const currency = items[0]?.price.currencyCode === "USD" ? "$" : items[0]?.price.currencyCode || "$";

  // Use the per-unit price passed from ProductPage (already bundle-adjusted)
  const getEffectivePrice = (item: typeof items[0]) => {
    return parseFloat(item.price.amount);
  };

  const totalPrice = items.reduce((sum, item) => sum + getEffectivePrice(item) * item.quantity, 0);

  // Free shipping on all orders
  const hasFreeShipping = true;

  useEffect(() => {
    if (isOpen) syncCart();
  }, [isOpen, syncCart]);

  const handleCheckout = () => {
    const checkoutUrl = getCheckoutUrl();
    if (checkoutUrl) {
      gtagBeginCheckout({
        items: items.map(i => ({
          item_id: i.variantId,
          item_name: i.product.node.title,
          price: parseFloat(i.price.amount),
          quantity: i.quantity,
        })),
        value: totalPrice,
        currency: items[0]?.price.currencyCode || "USD",
      });
      ttqInitiateCheckout({
        content_ids: items.map(i => i.variantId),
        value: totalPrice,
        currency: items[0]?.price.currencyCode || "USD",
      });
      trackInitiateCheckoutInternal({
        value: totalPrice,
        currency: items[0]?.price.currencyCode || "USD",
        num_items: totalItems,
      });
      klaviyoTrackStartedCheckout({
        value: totalPrice,
        currency: items[0]?.price.currencyCode || "USD",
        item_count: totalItems,
        items: items.map(i => ({
          product_id: i.variantId,
          product_name: i.product.node.title,
          price: parseFloat(i.price.amount),
          quantity: i.quantity,
        })),
      });
      window.location.href = checkoutUrl;
      closeCart();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && closeCart()}>
      <SheetContent side="right" className="flex w-full flex-col bg-background sm:max-w-md">
        <SheetHeader className="pb-2">
          <SheetTitle className="font-serif text-2xl font-semibold tracking-tight text-foreground">
            Your Bag ({totalItems})
          </SheetTitle>
          <SheetDescription className="sr-only">Shopping cart contents</SheetDescription>
        </SheetHeader>

        {/* Free shipping bar */}
        {items.length > 0 && (
          <div className="rounded-lg border border-border bg-secondary/50 px-4 py-3">
            <div className="flex items-center gap-2">
              <Truck className="h-4 w-4 flex-shrink-0 text-foreground" />
              <span className="text-xs font-medium text-foreground sm:text-sm">
                Free shipping on your order ✓
              </span>
            </div>
          </div>
        )}

        {/* Empty state */}
        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-5 text-center">
            <ShoppingBag className="h-14 w-14 text-muted-foreground/30" />
            <div>
              <p className="font-serif text-lg text-foreground">Your bag is empty</p>
              <p className="mt-1 text-sm text-muted-foreground">Discover something you'll love</p>
            </div>
            <Button
              variant="outline"
              onClick={closeCart}
              className="rounded-full border-foreground px-6 text-sm font-medium tracking-wide text-foreground hover:bg-foreground hover:text-background"
            >
              Continue Shopping
            </Button>
          </div>
        ) : (
          <>
            {/* Cart items */}
            <div className="flex-1 overflow-y-auto py-3">
              <AnimatePresence mode="popLayout">
                {items.map((item) => {
                  const image = item.product.node.images?.edges?.[0]?.node;
                  const itemCurrency = item.price.currencyCode === "USD" ? "$" : item.price.currencyCode;

                  return (
                    <motion.div
                      key={item.variantId}
                      layout
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="mb-1"
                    >
                      <div className="flex gap-3 py-3 sm:gap-4">
                        <div className="h-[72px] w-[56px] flex-shrink-0 overflow-hidden rounded-md bg-secondary sm:h-24 sm:w-20">
                          {image && (
                            <img
                              src={image.url}
                              alt={image.altText || item.product.node.title}
                              className="h-full w-full object-cover"
                            />
                          )}
                        </div>
                        <div className="flex min-w-0 flex-1 flex-col justify-between">
                          <div>
                            <h4 className="line-clamp-2 font-serif text-sm font-medium leading-snug text-foreground sm:text-base">
                              {item.product.node.title}
                            </h4>
                            {item.selectedOptions
                              .filter((o) => o.name !== "Title")
                              .map((o) => (
                                <p key={o.name} className="mt-0.5 line-clamp-1 text-[11px] text-muted-foreground sm:text-xs">
                                  {o.name}: {o.value}
                                </p>
                              ))}
                          </div>
                          <div className="mt-2 flex items-end justify-between gap-2">
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                                className="flex h-7 w-7 items-center justify-center rounded-full border border-border text-foreground outline-none transition-colors hover:bg-secondary"
                              >
                                <Minus className="h-3 w-3" />
                              </button>
                              <span className="w-6 text-center text-sm font-medium text-foreground">
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                                className="flex h-7 w-7 items-center justify-center rounded-full border border-border text-foreground outline-none transition-colors hover:bg-secondary"
                              >
                                <Plus className="h-3 w-3" />
                              </button>
                            </div>
                            <div className="flex flex-col items-end gap-0.5">
                              {(() => {
                                const unitPrice = parseFloat(item.price.amount);
                                const total = unitPrice * item.quantity;
                                const originalUnit = item.originalPrice ? parseFloat(item.originalPrice.amount) : null;
                                const savings = originalUnit ? (originalUnit - unitPrice) * item.quantity : 0;
                                return (
                                  <>
                                    <span className="text-sm font-semibold text-foreground">
                                      {itemCurrency}{total.toFixed(2)}
                                    </span>
                                    {savings > 0 && (
                                      <span className="text-[11px] font-medium text-accent">
                                        You saved {itemCurrency}{savings.toFixed(2)}
                                      </span>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => removeItem(item.variantId)}
                          className="mt-0.5 flex-shrink-0 self-start text-muted-foreground transition-colors hover:text-foreground"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <Separator />
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>

            {/* Upsell: 1 bottle → 2 bottles */}
            {totalItems === 1 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-3 rounded-xl border border-accent/20 bg-accent/5 px-4 py-3"
              >
                <div className="mb-1 flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-accent" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-accent">Most Popular</span>
                </div>
                <p className="text-[13px] leading-snug text-foreground">
                  Real barrier support takes consistency. Switch to <span className="font-semibold">The Ritual</span> — 2 bottles, save 30% + free shipping.
                </p>
                <button
                  onClick={() => {
                    const item = items[0];
                    if (item) updateQuantity(item.variantId, 2);
                  }}
                  className="mt-2 inline-flex rounded-full border border-foreground bg-foreground px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-background transition-colors hover:bg-foreground/90"
                >
                  Upgrade to The Ritual
                </button>
              </motion.div>
            )}

            {/* Upsell: 2 bottles → 3 bottles */}
            {totalItems === 2 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-3 rounded-xl border border-accent/20 bg-accent/5 px-4 py-3"
              >
                <div className="mb-1 flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-accent" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-accent">Best Value</span>
                </div>
                <p className="text-[13px] leading-snug text-foreground">
                  The biggest changes happen with consistent daily use. Go for <span className="font-semibold">The Stock-Up</span> — 3 bottles, save 38% + includes a free gift.
                </p>
                <button
                  onClick={() => {
                    const item = items[0];
                    if (item) updateQuantity(item.variantId, 3);
                  }}
                  className="mt-2 inline-flex rounded-full border border-foreground bg-foreground px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-background transition-colors hover:bg-foreground/90"
                >
                  Upgrade to The Stock-Up
                </button>
              </motion.div>
            )}

            {/* Footer */}
            <div className="border-t border-border pt-4">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Subtotal</span>
                <span className="text-sm font-medium text-foreground">
                  {currency}{totalPrice.toFixed(2)}
                </span>
              </div>
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Shipping</span>
                <span className="text-xs text-muted-foreground sm:text-sm">
                  {hasFreeShipping ? "Free" : "Calculated at checkout"}
                </span>
              </div>
              <Separator className="mb-4" />
              <div className="mb-5 flex items-center justify-between">
                <span className="font-serif text-lg font-semibold text-foreground">Total</span>
                <span className="font-serif text-lg font-semibold text-foreground">
                  {currency}{totalPrice.toFixed(2)}
                </span>
              </div>
              <Button
                onClick={handleCheckout}
                className="h-12 w-full rounded-full text-sm font-medium uppercase tracking-widest"
                size="lg"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <><Lock className="mr-2 h-4 w-4" /> Secured Checkout</>
                )}
              </Button>
              <div className="mt-4 flex items-center justify-center gap-4 text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] font-medium tracking-wide">60-Day Money-Back Guarantee. Free shipping on all orders.</span>
                </div>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
